import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react-v3";
import { ChangeEvent, KeyboardEvent, useState } from "react";
import { RiCloseLine } from "react-icons/ri";
import { SimpleModal } from "../patient-overview-v2-content/components/simple-modal";
import { useCustomKeywords } from "../patient-overview-v2-content/hooks/use-custom-keywords";

type CustomKeywordsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function CustomKeywordsModal({ isOpen, onClose }: CustomKeywordsModalProps) {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const { customKeywords, addKeyword, removeKeyword, clearAll } = useCustomKeywords();

  function handleAddKeyword() {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) {
      setError("Keyword cannot be empty");
      return;
    }
    const success = addKeyword(trimmedValue);
    if (!success) {
      setError("Keyword already exists");
      return;
    }
    setInputValue("");
    setError("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddKeyword();
    }
  }

  function handleClose() {
    setInputValue("");
    setError("");
    onClose();
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
    if (error) setError("");
  }

  function handleRemoveKeyword(keyword: string) {
    return () => removeKeyword(keyword);
  }

  return (
    <SimpleModal isOpen={isOpen} onClose={handleClose} maxWidth="600px">
      <SimpleModal.Header
        style={{
          backgroundColor: "var(--chakra-colors-bg-subtle)",
          borderColor: "var(--chakra-colors-border-subtle)",
        }}
      >
        <HStack justify="space-between" w="full">
          <Text fontWeight="bold" fontSize="lg" color="fg">
            Edit Custom Search Keywords
          </Text>
          <IconButton aria-label="Close" variant="ghost" size="sm" onClick={handleClose}>
            <RiCloseLine />
          </IconButton>
        </HStack>
      </SimpleModal.Header>

      <SimpleModal.Body>
        <VStack align="stretch" gap={4}>
          <Box>
            <Text color="fg" mb={2}>
              Add Keyword
            </Text>
            <Input
              placeholder="Type keyword and press Enter"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
            {error && (
              <Text color="status.error" fontSize="sm" mt={1}>
                {error}
              </Text>
            )}
            <Text color="fg.muted" fontSize="sm" mt={1}>
              Press Enter to add keyword to your list
            </Text>
          </Box>

          <Box>
            <HStack justify="space-between" mb={3}>
              <Text fontSize="sm" color="fg.muted">
                {customKeywords.length} custom keyword{customKeywords.length !== 1 ? "s" : ""}
              </Text>
              {customKeywords.length > 0 && (
                <Button size="xs" variant="ghost" colorPalette="red" onClick={clearAll}>
                  Clear All
                </Button>
              )}
            </HStack>
            {customKeywords.length > 0 && (
              <Flex gap={2} flexWrap="wrap">
                {customKeywords.map(keyword => (
                  <Tag.Root key={keyword} size="sm" colorPalette="blue" borderRadius="md">
                    <Tag.Label>{keyword}</Tag.Label>
                    <Tag.EndElement>
                      <Tag.CloseTrigger onClick={handleRemoveKeyword(keyword)} />
                    </Tag.EndElement>
                  </Tag.Root>
                ))}
              </Flex>
            )}
          </Box>
        </VStack>
      </SimpleModal.Body>

      <SimpleModal.Footer>
        <HStack gap={2} w="full" justify="flex-end">
          <Button
            bg="btn.info.bg"
            color="btn.info.color"
            size="sm"
            _hover={{ borderColor: "btn.info.hoverBorder" }}
            onClick={handleClose}
          >
            Done
          </Button>
        </HStack>
      </SimpleModal.Footer>
    </SimpleModal>
  );
}
